import { InvoiceStatus } from "@gymflow/types";
import {
  infiniteQueryOptions,
  mutationOptions,
  QueryKey,
} from "@tanstack/react-query";

import { clubAsPublicQueryKeys } from "../../../hooks/clubAsPublic";
import { api } from "../../api";
import { apiV2 } from "../../apiV2";
import { queryClient } from "../../QueryClient";
import { tokenPagination } from "../../tokenPagination";

const keys = {
  all: () => ["staffApiVoucher"] as QueryKey,
  list: (filter: unknown) => [...keys.all(), "list", filter] as QueryKey,
};

export const staffApiVoucher = {
  keys,
  queries: {},
  infiniteQueries: {
    list: (params: { limit?: number; nextPageToken?: string }) =>
      infiniteQueryOptions({
        queryKey: keys.list(params),
        queryFn: async ({ pageParam }) =>
          (
            await api.get("/voucher", {
              params: {
                ...params,
                nextPageToken: pageParam,
              },
            })
          ).data,
        ...tokenPagination,
      }),
  },
  mutations: {
    enableVouchers: () =>
      mutationOptions({
        mutationFn: async () => {
          return (await api.post("/voucher-settings")).data;
        },
        onSettled: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: clubAsPublicQueryKeys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: keys.all(),
            }),
          ]);
        },
      }),
    disableVouchers: () =>
      mutationOptions({
        mutationFn: async () => {
          return (await api.delete("/voucher-settings")).data;
        },
        onSettled: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: clubAsPublicQueryKeys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: keys.all(),
            }),
          ]);
        },
      }),
    order: () =>
      mutationOptions({
        mutationFn: async ({
          orderKey,
          paymentMethodId,
          ...params
        }: {
          orderKey: string;
          userMemberId: string;
          paymentMethodId: string;
          amount?: number;
        }) => {
          return (
            await api.put<{
              invoiceNumber: string;
              invoiceStatus: InvoiceStatus;
              voucherId: string;
              userId: string | undefined;
            }>(
              `/voucher-checkout/${orderKey}/payment-method/${paymentMethodId}`,
              {
                userMemberId: params.userMemberId,
                amount: params.amount,
              },
            )
          ).data;
        },
        onSettled: async (_, __, { userMemberId }) =>
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: apiV2.staff.voucher.keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.staff.member.keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.staff.wallet.keys.list(userMemberId),
            }),
          ]),
      }),
    customPayment: () =>
      mutationOptions({
        mutationFn: async ({
          orderKey,
          customPaymentCategoryId,
          ...params
        }: {
          orderKey: string;
          userMemberId: string;
          customPaymentCategoryId: number;
          amount?: number;
        }) => {
          return (
            await api.put<{
              invoiceNumber: string;
              invoiceStatus: InvoiceStatus;
              voucherId: string;
              userId: string | undefined;
            }>(
              `/voucher-checkout/${orderKey}/custom-payment/${customPaymentCategoryId}`,
              {
                userMemberId: params.userMemberId,
                amount: params.amount,
              },
            )
          ).data;
        },
        onSettled: async (_, __, { userMemberId }) =>
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: apiV2.staff.voucher.keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.staff.member.keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.staff.wallet.keys.list(userMemberId),
            }),
          ]),
      }),
  },
};
